/**
 * @fileoverview Soft-Reset ("Dar otra oportunidad") Modal for Personal Quick Reaction.
 * Allows operators to grant a retry to an individual participant with a mandatory audit rationale.
 * Generates a new One-Time Token (OTT) and preserves historical metrics in the audit log.
 * 
 * @module components/Admin/Overlays/QuickReactionPersonal/QuickReactionPersonalResetModal
 */

import React, { useState, useEffect } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { ResetUser, GetAdminOperatorName } from './../../../../utilities/BL/PersonalQuickReaction';

/**
 * Soft Reset Modal Component.
 * 
 * @function QuickReactionPersonalResetModal
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible.
 * @param {Object} [props.participant] - Target participant entity.
 * @param {Function} props.onClose - Close callback.
 * @param {Function} props.onResetSuccess - Callback invoked after successful reset.
 * @returns {React.ReactElement|null} Modal markup.
 */
export const QuickReactionPersonalResetModal = ({
    isOpen,
    participant,
    onClose,
    onResetSuccess
}) => {
    const { showMessage } = useFloatMessage();
    const [reason, setReason] = useState('');
    const [operatorName, setOperatorName] = useState('Administrador');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setReason('');
            GetAdminOperatorName().then(op => setOperatorName(op || 'Administrador'));
        }
    }, [isOpen]);

    if (!isOpen || !participant) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!reason.trim()) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'El motivo del reintento es obligatorio para fines de auditoría.'
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                participantId: participant.id,
                reason: reason.trim(),
                operator: operatorName.trim()
            };

            const res = await ResetUser(payload);
            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `¡Se ha concedido otra oportunidad a "${participant.participantName || participant.name}" exitosamente!`
                });
                if (onResetSuccess) onResetSuccess(res);
                onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res.message || 'Error al conceder reintento al participante.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: error.message || 'Error de conexión.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const reactionTimeDisplay = participant.reactionTimeMs != null
        ? `${participant.reactionTimeMs} ms`
        : 'Sin partida jugada';

    return (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 1060 }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 shadow-lg rounded-4">
                    {/* Header */}
                    <div className="modal-header bg-warning text-dark border-0">
                        <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                            <i className="bi bi-arrow-repeat fs-4"></i>
                            Dar otra oportunidad (Soft-Reset)
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>

                    {/* Body */}
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body p-4 bg-light">
                            <div className="card p-3 border-0 rounded-3 shadow-sm bg-white mb-3">
                                <h6 className="fw-bold text-dark mb-2">Datos del Aficionado</h6>
                                <div className="row g-2 small">
                                    <div className="col-6">
                                        <span className="text-muted d-block">Nombre:</span>
                                        <strong>{participant.participantName || participant.name}</strong>
                                    </div>
                                    <div className="col-6">
                                        <span className="text-muted d-block">Correo:</span>
                                        <strong>{participant.email}</strong>
                                    </div>
                                    <div className="col-6">
                                        <span className="text-muted d-block">Teléfono:</span>
                                        <strong>{participant.phone}</strong>
                                    </div>
                                    <div className="col-6">
                                        <span className="text-muted d-block">Tiempo Previo:</span>
                                        <strong className="text-danger">{reactionTimeDisplay}</strong>
                                    </div>
                                </div>
                            </div>

                            <div className="alert alert-warning py-2 px-3 small rounded-3 mb-3 d-flex align-items-center gap-2">
                                <i className="bi bi-info-circle-fill fs-5"></i>
                                <div>
                                    El registro previo se archivará en la bitácora de auditoría y se emitirá un nuevo One-Time Token para que el aficionado pueda jugar de nuevo.
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold text-dark small">
                                    Motivo del Reintento *
                                </label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    placeholder="Ej. Toque involuntario previo a la señal, desconexión de red o solicitud presencial."
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    required
                                    autoFocus
                                />
                            </div>

                            <div className="mb-1">
                                <label className="form-label fw-bold text-dark small">
                                    Operador Responsable
                                </label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={operatorName}
                                    onChange={(e) => setOperatorName(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="modal-footer bg-light border-top">
                            <button type="button" className="btn btn-outline-secondary fw-bold" onClick={onClose} disabled={isSubmitting}>
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-warning fw-bold d-flex align-items-center gap-2"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="spinner-border spinner-border-sm" role="status"></div>
                                        Procesando...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check2-circle"></i> Conceder Reintento
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
